import { DateTime } from 'luxon';
import { Proofs } from 'smg/customer-api/proofs';
import * as models from 'smg/customer-api/models';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { Debounce } from 'smg/util';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import { Relref } from '../../hugo';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

void main();

const proofMap = new Map<string, { created?: DateTime; el: HTMLElement }>();

function createProofCard(p: models.Proof): HTMLElement {
    if (!p.id) {
        const $div = document.createElement('div');
        $div.classList.add('d-none');
        return $div;
    }

    const existing = proofMap.get(p.id);
    if (existing) {
        if ((p.created?.toMillis() ?? 0) > (existing.created?.toMillis() ?? 0)) {
            updateProofElement(existing.el, p);
            moveProofElementToTop(existing.el);
            proofMap.set(p.id, { created: p.created, el: existing.el });
        }

        const $div = document.createElement('div');
        $div.classList.add('d-none');
        return $div;
    }

    const $proofsContainer = document.querySelector('#proofs-container') as HTMLElement;
    const $cardEl = template.Clone('#proof-card-template').firstElementChild as HTMLTemplateElement;

    const urls = GetProofURLs(window.cloudFront, p.file || '');
    const $imgLink = $cardEl.querySelector('.card-link') as HTMLAnchorElement;
    $imgLink.setAttribute('data-fancybox', 'proofs');
    $imgLink.href = urls.main;
    $imgLink.setAttribute('data-caption', p.name || '');
    const $img = $cardEl.querySelector('img') as HTMLImageElement;
    $img.src = urls.thumb || '';
    $img.alt = p.name || '';

    const $title = $cardEl.querySelector('.card-title') as HTMLElement;
    $title.textContent = p.name || 'Untitled Proof';

    $proofsContainer.appendChild($cardEl);

    const $viewDetailsBtn = $cardEl.querySelector('.view-proof-details') as HTMLAnchorElement;
    $viewDetailsBtn.href = `${Relref('/account/proof')}?id=${p.id}`;

    proofMap.set(p.id, { created: p.created, el: $cardEl });
    updateCount();

    return $cardEl;
}

function updateProofElement(el: HTMLElement, p: models.Proof) {
    const urls = GetProofURLs(window.cloudFront, p.file || '');
    const $imgLink = el.querySelector('.card-link') as HTMLAnchorElement;
    $imgLink.href = urls.main;
    const $img = el.querySelector('img') as HTMLImageElement;
    $img.src = urls.thumb || '';
    $img.alt = p.name || '';

    const $title = el.querySelector('.card-title') as HTMLElement;
    $title.textContent = p.name || 'Untitled Proof';
}

function moveProofElementToTop(el: HTMLElement) {
    const $container = document.querySelector('#proofs-container') as HTMLElement;
    if ($container.firstChild !== el) {
        $container.insertBefore(el, $container.firstChild);
    }
}

function updateCount() {
    const $headerEls = document.querySelector<HTMLElement>('.header-els');
    if ($headerEls) {
        const $headerTitleContainer = $headerEls.querySelector<HTMLElement>('.header-title-container');
        if ($headerTitleContainer) {
            const $shownCount = $headerTitleContainer.querySelector<HTMLElement>('.shown-count');
            if ($shownCount) {
                $shownCount.textContent = `${proofMap.size} items`;
            }
        }
    }
}

function toggleLoading(loading: boolean) {
    const $spinner = document.querySelector('#rows-spinner-parent') as HTMLElement;
    if (loading) {
        $spinner.classList.remove('d-none');
    } else {
        $spinner.classList.add('d-none');
    }
}

function showNotFound() {
    const $notFoundContainer = document.querySelector('#not-found-container') as HTMLElement;
    $notFoundContainer.classList.remove('d-none');
    const $proofsContainer = document.querySelector('#proofs-container') as HTMLElement;
    $proofsContainer.classList.add('d-none');
}

function showProofs() {
    const $notFoundContainer = document.querySelector('#not-found-container') as HTMLElement;
    $notFoundContainer.classList.add('d-none');
    const $proofsContainer = document.querySelector('#proofs-container') as HTMLElement;
    $proofsContainer.classList.remove('d-none');
}

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    toggleLoading(true);

    const $headerEls = document.querySelector<HTMLElement>('.header-els');
    if ($headerEls) {
        $headerEls.classList.add('flex-column', 'gap-2', 'gap-md-6');
        const $headerTitleContainer = $headerEls.querySelector<HTMLElement>('.header-title-container');
        if ($headerTitleContainer) {
            $headerTitleContainer.classList.add('justify-content-between', 'align-items-center');
        }
        const $shownCount = document.createElement('span');
        $shownCount.classList.add('shown-count');
        $shownCount.textContent = `${proofMap.size} items`;
        $headerTitleContainer?.appendChild($shownCount);

        const $searchContainer = $headerEls.querySelector<HTMLElement>('.search-container');
        if ($searchContainer) {
            $searchContainer.classList.remove('d-none');
        }
    }

    const $target = document.querySelector('#proofs-container') as HTMLElement | null;
    if (!$target) {
        return;
    }

    let hasNextPage = true;
    let loading = false;

    const pl: PageLoader<models.Proof> = new PageLoader<models.Proof>({
        clearResults: false,
        parent: $target,
        beforeLoad: () => {
            loading = true;

            if (pl.currentPage() === 0) {
                $target.innerHTML = '';
                proofMap.clear();
            }
            toggleLoading(true);
        },
        get: () => Proofs({
            sort: pl.sort() || 'created desc',
            max: 12,
            p: pl.currentPage(),
            q: pl.search(),
        }).then(resp => {
            hasNextPage = resp.nextPage;
            return {
                rows: resp.proofs,
                nextPage: resp.nextPage,
            };
        }),
        draw: (proof: models.Proof) => createProofCard(proof),
        afterLoad: () => {
            if (proofMap.size === 0) {
                showNotFound();
            } else {
                showProofs();
                updateCount();
            }
            toggleLoading(false);
            loading = false;
        },
    });

    pl.activate();
    pl.load();

    const checkScroll = () => {
        if (loading || !hasNextPage) {
            return;
        }

        const containerRect = $target.getBoundingClientRect();
        const bottomVisible = containerRect.bottom <= window.innerHeight;
        if (bottomVisible) {
            loading = true;
            pl.setPage(pl.currentPage() + 1);
            pl.load();
        }
    };

    const debouncedCheckScroll = Debounce(checkScroll, 200, false);

    window.addEventListener('scroll', debouncedCheckScroll);
    window.addEventListener('resize', debouncedCheckScroll);
}